import ScrollableContainer from '@src/components/common/container/ScrollableContainer';
import LemonIcon from '@src/components/common/image/LemonIcon';
import withLocalize, { IWithLocalizeOwnProps } from '@src/components/common/localize/withLocalize';
import ViewSectionItem from '@src/components/common/section/ViewSectionItem';
import HeaderTitle from '@src/components/course/common/HeaderTitle';
import { IColumnChartData, TimunColumnChart } from '@src/components/externalEducationApplication/statistics/charts/TimunColumnChart';
import { IPieData, TimunPieChart } from '@src/components/externalEducationApplication/statistics/charts/TimunPieChart';
import StatisticCardItem from '@src/components/externalEducationApplication/statistics/StatisticCardItem';
import ApplicationStatisticsUtils from '@src/components/externalEducationApplication/statistics/utils/ApplicationStatisticsUtils';
import { ExternalApplicationStatisticsCategories } from '@src/components/externalEducationApplication/statistics/utils/consts';
import { ActivityPhaseEnum } from '@src/model/activity/ActivityPhase';
import { IEducationCategory } from '@src/model/educationcategory/EducationCategory';
import { EmploymentStatusEnum } from '@src/model/externalEducationApplication/ExternalEducationApplication';
import { ICountPerStatus } from '@src/model/externalEducationApplication/ExternalEducationApplicationStatistics';
import { IUserCategory } from '@src/model/user/UserCategory';
import { Divider, Typography } from 'antd';
import React from 'react';

interface IEducationApplicationStatisticsCountOwnProps {
  numberOfApplications: number;
  countPerApplicationStatus: Array<ICountPerStatus<ActivityPhaseEnum>>;
  countPerEmploymentStatus: Array<ICountPerStatus<EmploymentStatusEnum>>;
  countPerEducationCategory?: Array<ICountPerStatus<IEducationCategory>>;
  countPerUserCategory?: Array<ICountPerStatus<IUserCategory>>;
}

type IEducationApplicationStatisticsCountProps = IEducationApplicationStatisticsCountOwnProps & IWithLocalizeOwnProps;

const EducationApplicationStatisticsCount = (props: IEducationApplicationStatisticsCountProps) => {

  const numberOfEmployedApplications = ApplicationStatisticsUtils.sumEducationApplicationCountPerRequestedEmplyomentStatusGroup(ExternalApplicationStatisticsCategories.employed, props.countPerEmploymentStatus);
  const numberOfUnemployedApplications = ApplicationStatisticsUtils.sumEducationApplicationCountPerRequestedEmplyomentStatusGroup(ExternalApplicationStatisticsCategories.unemplyoed, props.countPerEmploymentStatus);
  const numberOfApplicationsPerStatus: IColumnChartData[] = props.countPerApplicationStatus.reduce((accumulator: IColumnChartData[], currentValue) => {
    const phaseName = Object.keys(ActivityPhaseEnum).find((phase) => ActivityPhaseEnum[phase as keyof typeof ActivityPhaseEnum] === currentValue.type.id);
    return [...accumulator, { value: currentValue.count, label: props.translate(`EXTERNAL_EDUCATION_APPLICATION.PHASE.${phaseName}_LABEL`) }];
  }, []);

  const applicationsPerEmploymentStatusData: IPieData[] = [
    {
      label: props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_APPLICATIONS_EMPLOYED'),
      value: numberOfEmployedApplications,
    },
    {
      label: props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_APPLICATIONS_UNEMPLYOED'),
      value: numberOfUnemployedApplications,
    },
  ];

  const applicationsPerEducationCategoryData: IPieData[] | undefined = props.countPerEducationCategory?.map((educationCategoryStats) => {
    return (
      {
        value: educationCategoryStats.count,
        label: educationCategoryStats.type.name,
      }
    );
  });

  const applicationsPerUserCategoryData: IPieData[] | undefined = props.countPerUserCategory?.map((userCategoryStats) => {
    return (
      {
        value: userCategoryStats.count,
        label: userCategoryStats.type.name,
      }
    );
  });

  return (
    <ViewSectionItem
      header={<HeaderTitle title={props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_TITLE')} icon={<LemonIcon name="clipboard" size="large" />} />}
      collapsible={false}
      active={true}
      bordered={true}
    >
      <StatisticCardItem statisticalNumber={props.numberOfApplications} description={props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_TOTAL_COUNT_TITLE')} />

      <Divider />

      <Typography.Title level={3}> {props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_PER_APPLICATION_STATUS_TITLE')} </Typography.Title>
      <ScrollableContainer width={700}>
        <TimunColumnChart data={numberOfApplicationsPerStatus} />
      </ScrollableContainer>

      <Divider />

      <Typography.Title level={3}> {props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_PER_EMPLYOMENT_STATUS_TITLE')} </Typography.Title>
      <ScrollableContainer width={800}>
        <TimunPieChart data={applicationsPerEmploymentStatusData} />
      </ScrollableContainer>

      <Divider />

      <Typography.Title level={3}> {props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_PER_EDUCATION_CATEGORY_TITLE')} </Typography.Title>
      <ScrollableContainer width={800}>
        <TimunColumnChart data={applicationsPerEducationCategoryData ?? []} />
      </ScrollableContainer>

      <Divider />

      <Typography.Title level={3}> {props.translate('EXTERNAL_EDUCATION_APPLICATION_STATISTICS.STATISTICS_COUNT_PER_USER_CATEGORY_TITLE')} </Typography.Title>
      <ScrollableContainer width={800}>
        <TimunColumnChart data={applicationsPerUserCategoryData ?? []} />
      </ScrollableContainer>
    </ViewSectionItem>
  );
};

export default withLocalize<IEducationApplicationStatisticsCountOwnProps>(EducationApplicationStatisticsCount as any);
